import React, { createContext, useContext, useState, useEffect } from "react";

const PWAContext = createContext(null);

export const PWAProvider = ({ children }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);

  useEffect(() => {
    // Check if already running in standalone mode
    const checkIsStandalone = () => {
      const isStandalone =
        window.matchMedia("(display-mode: standalone)").matches ||
        window.navigator.standalone || // for iOS Safari
        document.referrer.includes("android-app://");
      setIsInstalled(isStandalone);
    };

    checkIsStandalone();

    // Listen for beforeinstallprompt event (fired by Chrome/Android/etc. when PWA is installable)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault(); // Prevent browser's automatic banner
      setDeferredPrompt(e);
      setIsInstallable(true);
      console.log("PWA: beforeinstallprompt event fired & saved.");
    };

    // Listen for appinstalled event
    const handleAppInstalled = (e) => {
      console.log("PWA: App installed successfully!");
      setDeferredPrompt(null);
      setIsInstallable(false);
      setIsInstalled(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const installApp = async () => {
    if (deferredPrompt) {
      console.log("PWA: Triggering programmatic install prompt...");
      deferredPrompt.prompt();
      
      const choiceResult = await deferredPrompt.userChoice;
      console.log(`PWA: User choice outcome: ${choiceResult.outcome}`);
      
      setDeferredPrompt(null);
      setIsInstallable(false);
    } else {
      // Prompt not available immediately (could be loading/registering or unsupported platform)
      const isChromeOrAndroid = /Chrome|CriOS|Android|Edg/i.test(navigator.userAgent);
      const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;

      if (isChromeOrAndroid && !isStandalone) {
        console.log("PWA: Prompt not ready yet on Chrome/Android. Showing loading info.");
        import("react-toastify").then(({ toast }) => {
          toast.info("Preparing app installer... Please wait a few seconds and try again.", {
            position: "top-center",
            autoClose: 3000
          });
        });
      } else {
        console.log("PWA: Prompt not available, opening manual install instructions modal...");
        setShowInstallModal(true);
      }
    }
  };

  return (
    <PWAContext.Provider
      value={{
        isInstallable,
        isInstalled,
        showInstallModal,
        setShowInstallModal,
        installApp
      }}
    >
      {children}
    </PWAContext.Provider>
  );
};

export const usePWA = () => {
  const context = useContext(PWAContext);
  if (!context) {
    throw new Error("usePWA must be used within a PWAProvider");
  }
  return context;
};
